import { shallowRef } from "vue";

declare global {
  var __RUNTIME_CONFIG__: RuntimeConfig | undefined;
}

export type RuntimeConfig = {
  VITE_API_CLIENT: string;
  VITE_MEDIA_URL: string;
  VITE_ENV_MODE: string;
  VITE_API_CLIENT_ID?: string;
  VITE_SAML_ENTITY_ID?: string;
  VITE_SAML_CONSUMER_URL?: string;
  VITE_SAML_LOGOUT_URL?: string;
  VITE_CALLBACK?: string;
  VITE_NDR_EMAIL?: string;
  VITE_GRAPH_IN_EMAIL?: string;
  VITE_ARC_SERVICE?: string;
  VITE_PHISH_SERVICE?: string;
  VITE_PHISING_SIMULATION_SERVICE?: string;
  VITE_SAT_SERVICE?: string;
  VITE_SEG_SERVICE?: string;
  VITE_PHISH_ORG_SERVICE?: string;
  VITE_COMMIT_SHA?: string;
  // Client-side Harness FME (Split) SDK key. When absent, harness flags run in
  // localhost mock mode (see createHarnessConfig).
  VITE_HARNESS_SDK_KEY?: string;
};

const REQUIRED_KEYS = ["VITE_API_CLIENT", "VITE_MEDIA_URL", "VITE_ENV_MODE"];

let cachedConfig: RuntimeConfig | null = null;
const runtimeConfigRef = shallowRef<RuntimeConfig | null>(null);

const validateRuntimeConfig = (data: unknown): RuntimeConfig => {
  if (!data || typeof data !== "object") {
    throw new Error("Runtime config must be an object.");
  }
  const record = data as Record<string, unknown>;
  const missing = REQUIRED_KEYS.filter(
    (key) => typeof record[key] !== "string" || record[key] === "",
  );
  if (missing.length) {
    throw new Error(
      `Missing required runtime config keys: ${missing.join(", ")}`,
    );
  }
  return record as RuntimeConfig;
};

export const loadRuntimeConfig = (): RuntimeConfig => {
  if (cachedConfig) return cachedConfig;

  const data = globalThis.__RUNTIME_CONFIG__;
  if (!data) {
    throw new Error("Runtime config is missing.");
  }
  cachedConfig = validateRuntimeConfig(data);
  runtimeConfigRef.value = cachedConfig;
  return cachedConfig;
};

export const getBaseUrl = () => import.meta.env.BASE_URL ?? "/";

export const getBasePath = () =>
  (getBaseUrl() || "/").replace(/\/+$/, "") || "/";

export const isDevBuild = () => Boolean(import.meta.env.DEV);

export const getRuntimeConfig = (): RuntimeConfig => {
  if (!cachedConfig) {
    throw new Error("Runtime config not loaded.");
  }
  return cachedConfig;
};

export const getApiClientUrl = () =>
  new URL(getRuntimeConfig().VITE_API_CLIENT, globalThis.location.origin)
    .toString()
    .replace(/\/$/, "");

export const getApiClientId = () => getRuntimeConfig().VITE_API_CLIENT_ID;

export const getMediaUrl = () => getRuntimeConfig().VITE_MEDIA_URL;

export const getSamlEntityId = () => getRuntimeConfig().VITE_SAML_ENTITY_ID;

export const getSamlConsumerUrl = () =>
  getRuntimeConfig().VITE_SAML_CONSUMER_URL;

export const getSamlLogoutUrl = () => getRuntimeConfig().VITE_SAML_LOGOUT_URL;

export const getCallbackUrl = () => getRuntimeConfig().VITE_CALLBACK;

export const getNdrEmail = () => getRuntimeConfig().VITE_NDR_EMAIL;

export const getGraphInEmail = () => getRuntimeConfig().VITE_GRAPH_IN_EMAIL;

export const getPhishOrgService = () =>
  getRuntimeConfig().VITE_PHISH_ORG_SERVICE;

export const useRuntimeConfig = () => runtimeConfigRef;

export const resetRuntimeConfigForTests = () => {
  cachedConfig = null;
  runtimeConfigRef.value = null;
  delete globalThis.__RUNTIME_CONFIG__;
};
